import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Crown, Trophy, Medal, Lock, CheckCircle2, Flame, GitBranch, X } from "lucide-react";
import api from "@/lib/api";
import { money } from "@/lib/format";

const RANK_ICONS = [Medal, Medal, Medal, Trophy, Trophy, Trophy, Crown, Crown, Crown, Crown];
const RANK_COLORS = [
  "#CD7F32", "#C0C0C0", "#FFD700", "#E5E4E2", "#0F52BA",
  "#E0115F", "#50C878", "#B9F2FF", "#9B59B6", "#F59E0B",
];

export default function Rewards() {
  const [ranks, setRanks] = useState([]);
  const [me, setMe] = useState(null);
  const [board, setBoard] = useState([]);
  const [royalty, setRoyalty] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/plan/config"), api.get("/me/dashboard"), api.get("/leaderboard"), api.get("/me/royalty-insight")])
      .then(([cfg, dash, lb, roy]) => { setRanks(cfg.data.ranks); setMe(dash.data); setBoard(lb.data); setRoyalty(roy.data); })
      .catch(() => toast.error("Load failed"));
  }, []);

  if (!me) return <div className="text-amber-400 font-mono-fin">Loading rewards...</div>;

  const myRank = me.user.rank || 0;
  const matched = me.stats.matched_business || 0;
  const next = ranks.find((r) => r.rank === myRank + 1);
  const curBase = ranks.find((r) => r.rank === myRank)?.matched || 0;
  const nextProgress = next ? Math.min(100, Math.max(0, ((matched - curBase) / (next.matched - curBase)) * 100)) : 100;
  const freshNeeded = next ? Math.max(0, next.matched - matched) : 0;

  return (
    <div className="space-y-8" data-testid="rewards-page">
      <div>
        <h1 className="font-display text-2xl font-bold flex items-center gap-2"><Crown className="w-6 h-6 text-amber-400" /> Salary Club · Ranks R1–R10</h1>
        <p className="text-slate-400 text-sm mt-1">Build balanced binary teams to unlock weekly leadership salaries.</p>
      </div>

      {/* current status */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-6" data-testid="rank-status">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl grid place-items-center" style={{ background: `${RANK_COLORS[myRank - 1] || "#333"}22`, border: `2px solid ${RANK_COLORS[myRank - 1] || "#555"}` }}>
              <Crown className="w-8 h-8" style={{ color: RANK_COLORS[myRank - 1] || "#888" }} />
            </div>
            <div>
              <div className="text-xs text-slate-400 uppercase tracking-wide">Current Rank</div>
              <div className="font-display text-2xl font-bold gold-text">{myRank ? ranks.find((r) => r.rank === myRank)?.name : "Unranked"}</div>
              <div className="text-xs text-slate-500 font-mono-fin mt-0.5">Matched: {money(matched, 0)}</div>
            </div>
          </div>
          {next && (
            <div className="w-full sm:w-64">
              <div className="flex justify-between text-xs mb-1"><span className="text-slate-400">Next: {next.name}</span><span className="font-mono-fin text-amber-300">{money(next.matched, 0)} total</span></div>
              <div className="h-3 rounded-full bg-black/50 overflow-hidden border border-amber-500/20">
                <motion.div className="h-full gold-btn" initial={{ width: 0 }} animate={{ width: `${nextProgress}%` }} transition={{ duration: 1 }} />
              </div>
              <div className="text-[11px] text-amber-300/90 font-mono-fin mt-1">Fresh business needed: {money(freshNeeded, 0)} more (of {money(next.fresh, 0)})</div>
              <div className="text-xs text-emerald-400 font-mono-fin mt-0.5">Weekly salary: {money(next.salary, 0)} × {next.weeks} wk</div>
            </div>
          )}
        </div>
      </motion.div>

      {/* rank badges */}
      <div>
        <h3 className="font-display font-bold text-lg mb-4">Rank Ladder</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {ranks.map((r, i) => {
            const Icon = RANK_ICONS[i] || Trophy;
            const color = RANK_COLORS[i] || "#888";
            const achieved = myRank >= r.rank;
            const isNext = myRank + 1 === r.rank;
            return (
              <motion.div
                key={r.rank}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.06 }}
                whileHover={{ y: -4 }}
                data-testid={`rank-badge-${r.rank}`}
                className={`relative rounded-2xl p-5 text-center overflow-hidden ${achieved ? "border-2" : "border border-slate-800"} ${isNext ? "ring-2 ring-amber-400/50" : ""}`}
                style={{ background: achieved ? `${color}14` : "#0D0E12", borderColor: achieved ? color : undefined }}
              >
                {achieved && <CheckCircle2 className="w-4 h-4 absolute top-3 right-3" style={{ color }} />}
                {isNext && <Flame className="w-4 h-4 absolute top-3 right-3 text-amber-400 animate-pulse" />}
                {!achieved && !isNext && <Lock className="w-3.5 h-3.5 absolute top-3 right-3 text-slate-600" />}
                <motion.div
                  className="w-14 h-14 rounded-full grid place-items-center mx-auto mb-3"
                  style={{ background: `${color}22`, border: `2px solid ${achieved ? color : "#333"}` }}
                  animate={isNext ? { scale: [1, 1.08, 1] } : {}}
                  transition={isNext ? { repeat: Infinity, duration: 1.8 } : {}}
                >
                  <Icon className="w-7 h-7" style={{ color: achieved || isNext ? color : "#555" }} />
                </motion.div>
                <div className="font-display font-bold text-sm" style={{ color: achieved ? color : "#94a3b8" }}>{r.name}</div>
                <div className="font-mono-fin text-xs text-slate-500 mt-1">{money(r.matched, 0)} total</div>
                <div className="font-mono-fin text-[10px] text-slate-600">+{money(r.fresh, 0)} fresh</div>
                <div className="font-mono-fin text-sm font-bold text-emerald-400 mt-2">{money(r.salary, 0)}/wk</div>
                <div className="font-mono-fin text-[10px] text-slate-500">{r.weeks} weeks</div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* royalty leg insight */}
      {royalty && (
        <div className="glass rounded-2xl p-6" data-testid="royalty-insight">
          <h3 className="font-display font-bold text-lg mb-1 flex items-center gap-2"><GitBranch className="w-5 h-5 text-amber-400" /> Royalty Leg Insight</h3>
          <p className="text-xs text-slate-400 mb-4">Unlock a royalty tier by having a rank achiever in BOTH legs — you then earn that % of their income.</p>
          <div className="grid sm:grid-cols-3 gap-3 mb-5">
            <div className="rounded-xl bg-black/30 border border-sky-500/20 p-3">
              <div className="text-[10px] text-slate-500 uppercase tracking-wide">Left Leg Top Rank</div>
              <div className="font-display font-bold text-sky-400">{royalty.left_max.name || "None yet"}</div>
            </div>
            <div className="rounded-xl bg-black/30 border border-amber-500/20 p-3">
              <div className="text-[10px] text-slate-500 uppercase tracking-wide">Right Leg Top Rank</div>
              <div className="font-display font-bold text-amber-400">{royalty.right_max.name || "None yet"}</div>
            </div>
            <div className="rounded-xl bg-black/30 border border-emerald-500/20 p-3" data-testid="royalty-unlocked">
              <div className="text-[10px] text-slate-500 uppercase tracking-wide">Royalty Unlocked</div>
              <div className="font-display font-bold text-emerald-400">{royalty.unlocked_rank ? `${royalty.unlocked_name} · ${royalty.unlocked_pct}%` : "Not yet"}</div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[420px]">
              <thead><tr className="text-amber-400 uppercase text-xs tracking-wider border-b border-amber-500/20">
                <th className="text-left py-2">Rank</th><th className="text-right py-2">Royalty</th><th className="text-center py-2">Left</th><th className="text-center py-2">Right</th><th className="text-center py-2">Unlocked</th>
              </tr></thead>
              <tbody>
                {royalty.tiers.map((t) => (
                  <tr key={t.rank} className="border-b border-slate-800/50" data-testid={`royalty-tier-${t.rank}`}>
                    <td className="py-2 font-medium">{t.name}</td>
                    <td className="py-2 text-right font-mono-fin text-amber-300">{t.royalty_pct}%</td>
                    <td className="py-2 text-center">{t.left ? <CheckCircle2 className="w-4 h-4 text-emerald-400 inline" /> : <X className="w-4 h-4 text-slate-600 inline" />}</td>
                    <td className="py-2 text-center">{t.right ? <CheckCircle2 className="w-4 h-4 text-emerald-400 inline" /> : <X className="w-4 h-4 text-slate-600 inline" />}</td>
                    <td className="py-2 text-center">{t.unlocked ? <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400 font-bold">YES</span> : <span className="text-xs text-slate-600">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* leaderboard */}
      <div className="glass rounded-2xl p-6" data-testid="leaderboard">
        <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-400" /> Top Earners Leaderboard</h3>
        {board.length === 0 ? <p className="text-slate-500 text-sm">No leaders yet — be the first to climb the ranks!</p> : (
          <div className="space-y-2">
            {board.map((b) => {
              const medal = b.position === 1 ? "#FFD700" : b.position === 2 ? "#C0C0C0" : b.position === 3 ? "#CD7F32" : null;
              return (
                <motion.div key={b.position} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: b.position * 0.04 }}
                  className="flex items-center gap-4 rounded-xl bg-black/30 border border-amber-500/10 p-3" data-testid={`leader-${b.position}`}>
                  <div className="w-8 h-8 rounded-full grid place-items-center font-mono-fin font-bold text-sm shrink-0"
                    style={{ background: medal ? `${medal}22` : "#1a1a22", color: medal || "#94a3b8", border: medal ? `1px solid ${medal}` : "none" }}>
                    {b.position}
                  </div>
                  <div className="flex-1 min-w-0"><div className="font-semibold text-sm">{b.name}</div><div className="text-xs text-slate-500">{b.rank_name}</div></div>
                  <div className="text-right"><div className="font-mono-fin text-sm text-sky-400">{money(b.matched_business, 0)}</div><div className="text-xs text-slate-500">matched</div></div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
